import apiService from './api.service';
import { ConsultancyProps } from '../interfaces/Consultancy';
import { ChartDataProps, ConsultancyDataProps, RefillDataProps, PaymentGraphData } from '../interfaces/Billing';

class BillingService {
  // Cache per i dati di billing
  private refillsCache: { data: RefillDataProps[] | null; timestamp: number } = { data: null, timestamp: 0 };
  private chartDataCache: Map<number, { data: ChartDataProps[]; timestamp: number }> = new Map();
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

  // Ottieni le ricariche dell'utente
  async getUserRefills(forceRefresh = false): Promise<{ results: RefillDataProps[]; count: number; next: string | null }> {
    if (!forceRefresh && this.refillsCache.data &&
      Date.now() - this.refillsCache.timestamp < this.CACHE_DURATION) {
      return {
        results: this.refillsCache.data,
        count: this.refillsCache.data.length,
        next: null
      };
    }

    const response = await apiService.get('/billing/refill/user-refills/');

    // Salva in cache
    this.refillsCache = {
      data: response.data.results,
      timestamp: Date.now()
    };

    return response.data;
  }

  // Ottieni i dati del grafico per un anno specifico
  async getChartData(year: number, forceRefresh = false): Promise<ChartDataProps[]> {
    const cacheKey = year;

    if (!forceRefresh) {
      const cached = this.chartDataCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }
    }

    const response = await apiService.get('/billing/chart-data/', {
      params: { year }
    });

    // Salva in cache
    this.chartDataCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });

    return response.data;
  }

  // Acquista crediti
  async buyCredits(planId: number): Promise<{ checkout_url: string }> {
    const response = await apiService.post('/billing/buy-credits/', { plan_id: planId });

    // Invalida la cache dei crediti
    this.invalidateRefillsCache();

    return response.data;
  }

  // Acquista una consultancy
  async buyConsultancy(consultingId: number, message?: string): Promise<void> {
    const response = await apiService.post('/billing/buy-consulting/', {
      consulting_id: consultingId,
      message
    });

    if (response.status === 402) {
      throw new Error('INSUFFICIENT_CREDITS');
    }

    return response.data;
  }

  // Acquista una consultancy gratuita
  async buyFreeConsultancy(pinnerProId: number, message?: string): Promise<void> {
    await apiService.post('/billing/buy-free-consulting/', {
      pinner_pro_id: pinnerProId,
      message
    });
  }

  // Ottieni le consultancy programmate
  async getScheduledConsultancies(userId: string): Promise<ConsultancyProps[]> {
    const response = await apiService.get(`/users/pinnerpro/${userId}/scheduled-consultancies/`);
    return response.data;
  }

  // Chiudi una consultancy
  async closeConsultancy(consultancyId: number, amount: number): Promise<void> {
    await apiService.post(`/consultancy/${consultancyId}/close/`, { amount });
  }

  // Ottieni i piani di billing disponibili
  async getBillingPlans(): Promise<ConsultancyDataProps[]> {
    const response = await apiService.get('/billing/plans/');
    return response.data;
  }

  // Conferma pagamento
  async confirmPayment(sessionId: string): Promise<void> {
    await apiService.post('/billing/confirm-payment/', { session_id: sessionId });

    // Invalida le cache dopo il pagamento
    this.invalidateRefillsCache();
  }

  // Spendi crediti per AI Chat
  async spendCreditsForChat(amount: number = 1): Promise<void> {
    await apiService.post('/billing/pay-ai/', {
      amount,
      service: 'ai_chat'
    });
  }

  // Ottieni i crediti dell'utente
  async getCredits(forceRefresh = false): Promise<{ credits: number }> {
    const response = await apiService.get('/billing/credits/');
    return response.data;
  }

  // Ottieni i dati del grafico pagamenti per un anno specifico
  async getPaymentsGraph(year: number): Promise<{ data: PaymentGraphData; status: number }> {
    const response = await apiService.get('/billing/payments-graph/', {
      params: { year }
    });
    return response;
  }

  // Invalida la cache
  invalidateRefillsCache(): void {
    this.refillsCache = { data: null, timestamp: 0 };
  }

  invalidateChartCache(year?: number): void {
    if (year) {
      this.chartDataCache.delete(year);
    } else {
      this.chartDataCache.clear();
    }
  }

  clearCache(): void {
    this.refillsCache = { data: null, timestamp: 0 };
    this.chartDataCache.clear();
  }
}

// Esporta istanza singleton
const billingService = new BillingService();
export default billingService;
